import { useState } from 'react';
import { T, Var, useGT } from 'gt-react';
import {
  ArTasksCloseOutcomeSchema,
  arTasksCloseOutcomesFor,
  type ArTaskRow,
  type ArTasksCloseOutcome,
} from '@erp/rpc';
import { useCloseArTasks } from '@/api/rpc/hooks/accounts-receivable/useArBillingTasks';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Spinner } from '@/components/ui/spinner';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from 'src/context/ToastContext';

type Translate = ReturnType<typeof useGT>;

function formatCloseOutcome(outcome: ArTasksCloseOutcome, gt: Translate) {
  switch (outcome) {
    case 'sent_outside_gently':
      return gt('Sent outside Gently', {
        context: 'AR task close outcome, invoice went out another way',
      });
    case 'invoiced_elsewhere':
      return gt('Invoiced elsewhere', {
        context: 'AR task close outcome, order was invoiced in another system',
      });
    case 'no_charge':
      return gt('No charge', {
        context: 'AR task close outcome, nothing to bill',
      });
    case 'duplicate':
      return gt('Duplicate', {
        context: 'AR task close outcome, duplicate or test record',
      });
  }
}

/**
 * Close the selected AR billing tasks with one outcome. Same shape as the
 * cash receipts close dialog: outcome picker, optional note.
 */
export function ArTasksCloseDialog({
  open,
  onOpenChange,
  tasks,
  onClosed,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tasks: Array<Pick<ArTaskRow, 'taskId' | 'kind'>>;
  onClosed: () => void;
}) {
  const taskIds = tasks.map((task) => task.taskId);
  // Only outcomes that fit every selected kind, e.g. a mixed selection gets
  // "No charge" and "Duplicate" but not "Sent outside Gently".
  const outcomeOptions = arTasksCloseOutcomesFor([
    ...new Set(tasks.map((task) => task.kind)),
  ]);
  const gt = useGT();
  const { showToast } = useToast();
  const closeTasks = useCloseArTasks();
  const [outcome, setOutcome] = useState<ArTasksCloseOutcome | null>(null);
  const [note, setNote] = useState('');

  function reset() {
    setOutcome(null);
    setNote('');
    onOpenChange(false);
  }

  function handleConfirm() {
    if (!outcome || taskIds.length === 0) return;
    closeTasks.mutate(
      { taskIds, outcome, note: note.trim() || undefined },
      {
        onSuccess: (result) => {
          reset();
          onClosed();
          showToast({
            color: 'success',
            title: gt('Tasks closed', {
              context: 'toast title after closing AR tasks',
            }),
            message: gt('{closed} closed as {outcome}', {
              context: 'toast message after closing AR tasks',
              closed: String(result.closed),
              outcome: formatCloseOutcome(outcome, gt),
            }),
          });
        },
        onError: (err) =>
          showToast({
            color: 'error',
            title: gt('Could not close tasks', {
              context: 'toast title when closing AR tasks fails',
            }),
            message: err.message,
          }),
      },
    );
  }

  function handleOpenChange(next: boolean) {
    if (!next && !closeTasks.isPending) reset();
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {taskIds.length === 1 ? (
              <T context="dialog title, close one AR task">Close Task</T>
            ) : (
              <T context="dialog title, close the selected AR tasks">
                Close <Var>{taskIds.length}</Var> Tasks
              </T>
            )}
          </DialogTitle>
          <DialogDescription>
            <T context="dialog description, close AR tasks">
              Each selected task closes as Completed with the outcome you
              choose.
            </T>
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-1">
          <div className="space-y-1.5">
            <Label htmlFor="ar-task-close-outcome">
              <T context="label, outcome picker for closing AR tasks">
                Outcome
              </T>
            </Label>
            <Select
              value={outcome}
              onValueChange={(value) => {
                const parsed = ArTasksCloseOutcomeSchema.safeParse(value);
                if (parsed.success) setOutcome(parsed.data);
              }}
            >
              <SelectTrigger id="ar-task-close-outcome">
                <SelectValue
                  placeholder={gt('Choose an outcome', {
                    context: 'placeholder, outcome picker for closing AR tasks',
                  })}
                />
              </SelectTrigger>
              <SelectContent>
                {outcomeOptions.map((option) => (
                  <SelectItem key={option} value={option}>
                    {formatCloseOutcome(option, gt)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ar-task-close-note">
              <T context="label, optional note when closing AR tasks">
                Note (optional)
              </T>
            </Label>
            <Textarea
              id="ar-task-close-note"
              value={note}
              maxLength={2000}
              onChange={(event) => setNote(event.target.value)}
              rows={3}
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={closeTasks.isPending}
          >
            <T context="dialog cancel button, close AR tasks">Cancel</T>
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!outcome || closeTasks.isPending}
          >
            {closeTasks.isPending && <Spinner className="mr-2 h-4 w-4" />}
            {taskIds.length === 1 ? (
              <T context="dialog confirm button, close one AR task">
                Close Task
              </T>
            ) : (
              <T context="dialog confirm button, close AR tasks">Close Tasks</T>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
